import { Button, ButtonColors, ButtonSizes, ButtonVariants } from "@inno-ui/button";
import { Icon, IconSizes, PlusIcon } from "@inno-ui/icons";
import { toast } from "@inno-ui/toast";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import bodyHoldingClipboard from "../../../../assets/boy-holding-clipboard.png";
import { useGetAvailableTimeSlots } from "../../../../hooks/api/use-get-available-time-slots";
import { useAppointmentStore } from "../../../../store/appointment/appointment-store";
import { useGeneralStore } from "../../../../store/general/general-store";
import { formatDate, formatTimezone } from "../../../../utils/helpers/date";
import styles from "./appointment-confirmation.module.scss";

export function AppointmentConfirmation() {
  const {
    userAppointmentData,
    appointmentData: { date, timeRange },
    resetOtherParticipants,
    resetTimeRange,
    setTimeIntervals,
    dateToDisplay,
  } = useAppointmentStore();
  const { currentTimezone } = useGeneralStore();
  const { reset } = useGeneralStore();
  const { t } = useTranslation();
  const { refetch, isLoading: isApiLoading } = useGetAvailableTimeSlots(dateToDisplay);
  const [isRescheduling, setIsRescheduling] = useState(false);

  const handleReschedule = async () => {
    if (date && timeRange.length === 2) {
      try {
        setIsRescheduling(true);
        // Revalidate available slots through API
        const { data } = await refetch();
        if (data?.data) {
          // Update the time intervals in the store with fresh data
          setTimeIntervals(data.data);
          // Reset the form to first step
          reset?.();
          resetTimeRange();
          resetOtherParticipants();
        }
      }
      catch (e) {
        toast.error({ title: `Failed to refresh time slots, ${e}` });
      }
      finally {
        setIsRescheduling(false);
      }
    }
  };

  return (
    <div className={styles.confirmationWrapper}>
      <div className={styles.confirmationHeader}>
        <img src={bodyHoldingClipboard} alt="boy holding clipboard" />
        <div className={styles.confirmationHeaderText}>
          <Icon icon="CircleCheckmarkFullIcon" size={IconSizes.Size24} color="var(--warning-green-light)" />
          <h2>{t("appointment-confirmation")}</h2>
        </div>
      </div>
      <div className={styles.confirmationBody}>
        <h3>{userAppointmentData?.extra?.event_type?.title}</h3>
        <div className={styles.confirmationBodyItemsWrapper}>
          <div className={styles.confirmationBodyItem}>
            <Icon icon="PersonLineIcon" />
            <p>
              {userAppointmentData?.extra?.event_type?.user_name}
              ,
              {" "}
              {" "}
              <span>{userAppointmentData?.extra?.event_type?.user_title}</span>
            </p>
          </div>
          <div className={styles.confirmationBodyItem}>
            <Icon icon="CalendarSimpleLineIcon" />
            <p>{!!date && formatDate(date, currentTimezone)}</p>
          </div>
          <div className={styles.confirmationBodyItem}>
            <Icon icon="ClockLineIcon" />
            <p>{!!timeRange && `${timeRange.join(" - ")}`}</p>
          </div>
          <div className={styles.confirmationBodyItem}>
            <Icon icon="GlobeLineIcon" />
            <p>
              {formatTimezone(currentTimezone)}
            </p>
          </div>
        </div>

        <div className={styles.confirmationSubText}>{t("receive-email")}</div>
      </div>
      <div className={styles.confirmationFooter}>
        <Button
          icon={PlusIcon}
          width="100%"
          label={t("reschedule")}
          size={ButtonSizes.L}
          color={ButtonColors.Blue}
          onClick={handleReschedule}
          isLoading={isRescheduling || isApiLoading}
          variant={ButtonVariants.Light}
        />
      </div>
    </div>
  );
}
